import React, { useState } from 'react';
import { Box, Typography, Chip, Button, TextField, Stack, Alert } from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Close as CloseIcon } from '@mui/icons-material';

// Colores estándar de clasificación (compartidos por todas las tablas de baremos)
export const classificationColors: Record<string, string> = {
  'Excepcionalmente Alto': '#dc6eee',
  'Sobre el Promedio': '#2196F3',
  'Promedio Alto': '#6dc7f0',
  'Promedio': '#4CAF50',
  'Promedio Bajo': '#FFEB3B',
  'Bajo el Promedio': '#FF9800',
  'Excepcionalmente Bajo': '#F44336',
};

export interface EvaluationSelectionData {
  label: string; // contexto de la celda, ej. "Edad 53-57" o "Métrica CON"
  scoreValue: number | string | null;
  units: string;
  percentile: number | string;
  classifications: string[];
  conversions: any[];
}

export interface EditableField {
  key: string;
  label: string;
  value: number | string | null;
  type?: 'number' | 'text';
}

interface EvaluationSelectionPanelProps {
  selection: EvaluationSelectionData | null;
  /** Solo admins: habilita el botón "Editar" para corregir el valor de la celda seleccionada. */
  editable?: boolean;
  /** Campos editables de la fila seleccionada (ej. score_min, score_max, percentile_min, percentile_max). */
  editableFields?: EditableField[];
  /** Se llama al guardar; debe hacer el PATCH y propagar el resultado hacia arriba. */
  onSaveEdit?: (values: Record<string, string | number | null>) => Promise<void>;
}

const formatScore = (scoreValue: number | string | null): string | null => {
  if (scoreValue === null || scoreValue === undefined) return null;
  if (typeof scoreValue === 'number') {
    return scoreValue % 1 === 0 ? String(scoreValue) : scoreValue.toFixed(2);
  }
  return String(scoreValue);
};

/**
 * Panel de "valor seleccionado" que se muestra debajo de la tabla de baremos
 * al hacer clic en una celda. Estructura estándar compartida por todos los
 * tests (basada en el diseño de Conteo de Elevator 3B).
 *
 * Para el modo de edición (solo admin): el padre debe montar este componente
 * con `key={identidadDeLaCelda}` para que React reinicie el estado interno de
 * edición cada vez que cambia la celda seleccionada.
 */
const EvaluationSelectionPanel: React.FC<EvaluationSelectionPanelProps> = ({
  selection,
  editable = false,
  editableFields,
  onSaveEdit,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftValues, setDraftValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  if (!selection) return null;

  const { label, scoreValue, units, percentile, classifications, conversions } = selection;
  const formattedScore = formatScore(scoreValue);

  const startEditing = () => {
    const initial: Record<string, string> = {};
    (editableFields || []).forEach((field) => {
      initial[field.key] = field.value === null || field.value === undefined ? '' : String(field.value);
    });
    setDraftValues(initial);
    setSaveError(null);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!onSaveEdit || !editableFields) return;
    setSaving(true);
    setSaveError(null);
    try {
      const values: Record<string, string | number | null> = {};
      editableFields.forEach((field) => {
        const raw = draftValues[field.key];
        if (field.type === 'text') {
          values[field.key] = raw === '' ? null : raw;
        } else {
          values[field.key] = raw === '' ? null : Number(raw);
        }
      });
      await onSaveEdit(values);
      setIsEditing(false);
    } catch (err: any) {
      setSaveError(err?.response?.data?.message || 'Error al guardar el cambio');
    } finally {
      setSaving(false);
    }
  };

  if (isEditing) {
    return (
      <Box
        sx={{
          mb: 2,
          p: 2,
          backgroundColor: 'rgba(255, 152, 0, 0.08)',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'warning.light',
        }}
      >
        <Typography variant="body2" color="warning.dark" sx={{ mb: 1.5 }}>
          <strong>Editando:</strong> {label}
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          {(editableFields || []).map((field) => (
            <TextField
              key={field.key}
              label={field.label}
              type={field.type === 'text' ? 'text' : 'number'}
              size="small"
              value={draftValues[field.key] ?? ''}
              onChange={(e) => setDraftValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
              sx={{ minWidth: 140 }}
            />
          ))}
        </Stack>
        {saveError && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {saveError}
          </Alert>
        )}
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            color="warning"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
          <Button size="small" variant="outlined" startIcon={<CloseIcon />} onClick={cancelEditing} disabled={saving}>
            Cancelar
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'success.light',
      }}
    >
      <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ mb: conversions.length > 0 ? 1.5 : 0 }}>
        <Typography variant="body2" color="success.dark">
          <strong>Seleccionado:</strong>
        </Typography>
        <Typography variant="body2" color="text.primary">
          {label}
        </Typography>
        {formattedScore !== null && (
          <Typography variant="body2" color="text.primary">
            → <strong>{formattedScore}</strong>{units ? ` ${units}` : ''}
          </Typography>
        )}
        <Typography variant="body2" color="text.primary">
          → Percentil {percentile}
        </Typography>
        {classifications.length > 0 && (
          classifications.length > 1 ? (
            <Chip
              label={classifications.join(' / ')}
              size="small"
              variant="filled"
              sx={{
                background: `linear-gradient(90deg, ${classificationColors[classifications[0]] || '#757575'} 0%, ${classificationColors[classifications[1]] || '#757575'} 100%)`,
                color: 'white',
                fontWeight: 'bold',
              }}
            />
          ) : (
            <Chip
              label={classifications[0]}
              size="small"
              variant="filled"
              sx={{ bgcolor: classificationColors[classifications[0]] || '#757575', color: 'white', fontWeight: 'bold' }}
            />
          )
        )}
        {editable && editableFields && editableFields.length > 0 && (
          <Button size="small" variant="outlined" color="warning" startIcon={<EditIcon />} onClick={startEditing} sx={{ ml: 'auto' }}>
            Editar
          </Button>
        )}
      </Box>

      {conversions.length > 0 && (
        <Box sx={{ pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Conversiones de Puntajes:
          </Typography>
          {/* Si el percentil cae en el límite entre 2 clasificaciones, las conversiones
              se muestran una al lado de la otra (row + wrap) en vez de apiladas. */}
          <Box display="flex" gap={1.5} flexWrap="wrap">
            {conversions.map((conversion, index) => (
              <Box
                key={index}
                sx={{
                  flex: '1 1 220px',
                  minWidth: 220,
                  p: 1.5,
                  backgroundColor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {/* Con doble clasificación, cada conversión indica a cuál de las 2 pertenece */}
                {conversions.length > 1 && conversion.classification && (
                  <Chip
                    label={conversion.classification}
                    size="small"
                    variant="filled"
                    sx={{
                      mb: 1,
                      bgcolor: classificationColors[conversion.classification] || '#757575',
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  />
                )}
                <Box display="flex" gap={2} flexWrap="wrap">
                  {conversion.zScore?.average != null && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Puntaje Z
                      </Typography>
                      <Typography variant="body2" color="text.primary" fontWeight="medium">
                        {conversion.zScore.average.toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  {conversion.tScore?.average != null && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Puntaje T
                      </Typography>
                      <Typography variant="body2" color="text.primary" fontWeight="medium">
                        {conversion.tScore.average.toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  {conversion.standardScore?.average != null && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Puntaje Estándar
                      </Typography>
                      <Typography variant="body2" color="text.primary" fontWeight="medium">
                        {conversion.standardScore.average}
                      </Typography>
                    </Box>
                  )}
                  {conversion.scaledScore?.average != null && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Puntaje Escalar
                      </Typography>
                      <Typography variant="body2" color="text.primary" fontWeight="medium">
                        {conversion.scaledScore.average}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default EvaluationSelectionPanel;
